import React, { useState } from 'react'
import { Box, Collapse, Stack, Typography, alpha } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import type { HolidayItineraryDay } from '@/src/types/holidays'

import HolidayImageView from '../holidayImage'
import ImageCarouselWrapper from '../imageCarousel'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

export interface ItineraryDayView extends HolidayItineraryDay {
    /** Just the numeral from `dayLabel` — the chip prints the word itself. */
    dayNumber: string
    experienceCount: number
}

interface ItineraryTimelineProps {
    days: ItineraryDayView[]
    /** Indexes currently open. More than one at a time once "Expand all" is used. */
    expanded: ReadonlySet<number>
    onToggleDay: (index: number) => void
    title: string
    subtitle: string
    toggleAllLabel: string
    onToggleAll: () => void
    experienceCountLabel: (count: number) => string
    photosLabel: (count: number) => string
    runningOrderLabel: string
    stepsShowMoreLabel: (count: number) => string
    stepsShowLessLabel: string
    isMobile: boolean
}

/**
 * Each day gets its own hue so the list reads as a sequence rather than a stack of identical
 * rows, cycling when a trip runs longer than the palette.
 */
const DAY_TONES = ['warning', 'primary', 'success', 'secondary', 'info', 'error'] as const

/** Thumbnails shown on a collapsed row; the rest are counted into the last one. */
const STACK_LIMIT = 3

/** Steps shown before a day's running order folds; five is a screenful on a phone. */
const STEP_PREVIEW = 5

/**
 * The day's running order, as a line of stops rather than a list of sentences — the first one
 * marked in the day's own hue so the eye starts where the day does.
 */
const DayRunningOrder: React.FC<{
    items: string[]
    label: string
    showMoreLabel: (count: number) => string
    showLessLabel: string
    isMobile: boolean
}> = ({ items, label, showMoreLabel, showLessLabel, isMobile }) => {
    const [expanded, setExpanded] = useState(false)

    const isCollapsible = items.length > STEP_PREVIEW
    const visible = isCollapsible && !expanded ? items.slice(0, STEP_PREVIEW) : items

    return (
        <Stack spacing={1}>
            <Typography
                sx={{
                    fontSize: '0.688rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'textColors.subheading'
                }}
            >
                {label}
            </Typography>

            <Stack>
                {visible.map((item, index) => {
                    const isLast = index === visible.length - 1

                    return (
                        <Stack key={item} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    flexShrink: 0,
                                    width: 14,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignSelf: 'stretch'
                                }}
                            >
                                {/* The thread between stops; the last one ends the line. */}
                                {!isLast && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            bottom: -4,
                                            width: '2px',
                                            bgcolor: 'borders.default'
                                        }}
                                    />
                                )}
                                <Box
                                    sx={{
                                        mt: '5px',
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: index === 0 ? 'secondary.main' : 'borders.heavy',
                                        bgcolor: 'background.paper'
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    pb: isLast ? 0 : 1.5,
                                    fontSize: isMobile ? '0.875rem' : '0.938rem',
                                    lineHeight: 1.5,
                                    color: 'textColors.primary'
                                }}
                            >
                                {item}
                            </Typography>
                        </Stack>
                    )
                })}
            </Stack>

            {isCollapsible && (
                <Stack
                    component="button"
                    type="button"
                    direction="row"
                    spacing={0.25}
                    onClick={() => setExpanded(current => !current)}
                    sx={{
                        alignItems: 'center',
                        alignSelf: 'flex-start',
                        // Lines up with the text, not the markers.
                        marginInlineStart: '26px',
                        border: 0,
                        p: 0,
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontSize: '0.938rem',
                        fontWeight: 700
                    }}
                >
                    <span>{expanded ? showLessLabel : showMoreLabel(items.length - STEP_PREVIEW)}</span>
                    {expanded ? (
                        <ExpandLessIcon sx={{ fontSize: 20 }} />
                    ) : (
                        <ExpandMoreIcon sx={{ fontSize: 20 }} />
                    )}
                </Stack>
            )}
        </Stack>
    )
}

const ItineraryTimeline: React.FC<ItineraryTimelineProps> = ({
    days,
    expanded,
    onToggleDay,
    title,
    subtitle,
    toggleAllLabel,
    onToggleAll,
    experienceCountLabel,
    photosLabel,
    runningOrderLabel,
    stepsShowMoreLabel,
    stepsShowLessLabel,
    isMobile
}) => {
    return (
        <Stack spacing={isMobile ? 1.5 : 2}>
            {/* The section names itself: the API titles this block with the destination, which the
                page has already said twice by the time a reader reaches the days. */}
            <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}
            >
                <Box>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: isMobile ? '1.25rem' : '1.625rem',
                            fontWeight: 700,
                            lineHeight: 1.3,
                            color: 'textColors.primary'
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.25,
                            fontSize: isMobile ? '0.875rem' : '1rem',
                            color: 'textColors.secondary'
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>

                {days.length > 1 && (
                    <Box
                        component="button"
                        type="button"
                        onClick={onToggleAll}
                        sx={{
                            flexShrink: 0,
                            border: 0,
                            p: 0,
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            color: 'primary.main',
                            fontSize: isMobile ? '0.875rem' : '0.938rem',
                            fontWeight: 700
                        }}
                    >
                        {toggleAllLabel}
                    </Box>
                )}
            </Stack>

            {/* Each day is its own card: one shared box made a seven-day trip read as a table. */}
            <Stack spacing={isMobile ? 1 : 1.25}>
                {days.map((day, index) => {
                    const isExpanded = expanded.has(index)
                    const panelId = `itinerary-day-panel-${index}`
                    const tone = DAY_TONES[index % DAY_TONES.length]
                    const images = day.images ?? []
                    const stack = images.slice(0, STACK_LIMIT)
                    const overflow = images.length - stack.length

                    return (
                        <Box
                            key={day.dayLabel + day.title}
                            id={`itinerary-day-${index}`}
                            sx={{
                                // Clears the sticky section bar, as the section headings do.
                                scrollMarginTop: isMobile ? '132px' : '124px',
                                borderRadius: HOLIDAYS_RADIUS.NESTED,
                                border: '1px solid',
                                borderColor: isExpanded ? 'borders.heavy' : 'borders.default',
                                bgcolor: 'background.paper',
                                overflow: 'hidden',
                                transition: 'border-color 0.2s'
                            }}
                        >
                            <Box
                                component="button"
                                type="button"
                                onClick={() => onToggleDay(index)}
                                aria-expanded={isExpanded}
                                aria-controls={panelId}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: isMobile ? 1.25 : 2,
                                    textAlign: 'start',
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    cursor: 'pointer',
                                    px: isMobile ? 1.5 : 2.5,
                                    py: isMobile ? 1.5 : 2,
                                    font: 'inherit',
                                    color: 'inherit',
                                    '&:hover': { bgcolor: 'neutral.100' }
                                }}
                            >
                                {/* DAY / n, tinted with this day's hue. */}
                                <Stack
                                    sx={theme => ({
                                        flexShrink: 0,
                                        width: isMobile ? 48 : 56,
                                        py: 0.75,
                                        alignItems: 'center',
                                        borderRadius: HOLIDAYS_RADIUS.NESTED,
                                        bgcolor: alpha(theme.palette[tone].main, 0.1),
                                        color: `${tone}.main`
                                    })}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '0.625rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {day.dayLabel.replace(/\s*\d+\s*/, '')}
                                    </Typography>
                                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}>
                                        {day.dayNumber}
                                    </Typography>
                                </Stack>

                                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: isMobile ? '1rem' : '1.125rem',
                                            fontWeight: 700,
                                            color: 'textColors.primary',
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {day.title}
                                    </Typography>
                                    {day.experienceCount > 0 && (
                                        <Typography
                                            sx={{ fontSize: '0.875rem', color: 'textColors.subheading' }}
                                        >
                                            {experienceCountLabel(day.experienceCount)}
                                        </Typography>
                                    )}
                                </Stack>

                                {/* A fanned stack of the day's photos, the last one counting the rest. */}
                                {stack.length > 0 && !isMobile && (
                                    <Stack direction="row" sx={{ flexShrink: 0, pe: 1 }}>
                                        {stack.map((image, imageIndex) => {
                                            const isLast = imageIndex === stack.length - 1
                                            const showOverflow = isLast && overflow > 0

                                            return (
                                                <Box
                                                    key={image.url}
                                                    sx={{
                                                        position: 'relative',
                                                        width: 54,
                                                        height: 64,
                                                        marginInlineStart: imageIndex === 0 ? 0 : '-16px',
                                                        borderRadius: HOLIDAYS_RADIUS.NESTED,
                                                        overflow: 'hidden',
                                                        border: '2px solid',
                                                        borderColor: 'background.paper',
                                                        bgcolor: 'neutral.100',
                                                        boxShadow: 1
                                                    }}
                                                >
                                                    <HolidayImageView image={image} isMobile={isMobile} />
                                                    {showOverflow && (
                                                        <Stack
                                                            sx={theme => ({
                                                                position: 'absolute',
                                                                inset: 0,
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                bgcolor: alpha(theme.palette.neutral.black, 0.6),
                                                                color: 'common.white',
                                                                fontSize: '0.875rem',
                                                                fontWeight: 700
                                                            })}
                                                        >
                                                            +{overflow}
                                                        </Stack>
                                                    )}
                                                </Box>
                                            )
                                        })}
                                    </Stack>
                                )}

                                <ExpandMoreIcon
                                    sx={{
                                        flexShrink: 0,
                                        color: 'textColors.subheading',
                                        transition: 'transform 0.2s',
                                        transform: isExpanded ? 'rotate(180deg)' : 'none'
                                    }}
                                />
                            </Box>

                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Stack
                                    id={panelId}
                                    spacing={isMobile ? 1.5 : 2}
                                    sx={{ px: isMobile ? 1.5 : 2.5, pb: isMobile ? 2 : 2.5 }}
                                >
                                    {day.highlights.length > 0 && (
                                        <DayRunningOrder
                                            items={day.highlights}
                                            label={runningOrderLabel}
                                            showMoreLabel={stepsShowMoreLabel}
                                            showLessLabel={stepsShowLessLabel}
                                            isMobile={isMobile}
                                        />
                                    )}

                                    {images.length > 0 && (
                                        <Stack spacing={0.75}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.688rem',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.1em',
                                                    textTransform: 'uppercase',
                                                    color: 'textColors.subheading'
                                                }}
                                            >
                                                {photosLabel(images.length)}
                                            </Typography>

                                            {isMobile ? (
                                                <ImageCarouselWrapper images={images} />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                                        gap: 1
                                                    }}
                                                >
                                                    {images.slice(0, 3).map(image => (
                                                        <Box
                                                            key={image.url}
                                                            sx={{
                                                                gridColumn: images.length === 1 ? 'span 2' : 'span 1',
                                                                aspectRatio: '3 / 2',
                                                                borderRadius: HOLIDAYS_RADIUS.NESTED,
                                                                overflow: 'hidden',
                                                                bgcolor: 'neutral.100'
                                                            }}
                                                        >
                                                            <HolidayImageView image={image} isMobile={false} />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Stack>
                                    )}
                                </Stack>
                            </Collapse>
                        </Box>
                    )
                })}
            </Stack>
        </Stack>
    )
}

export { ItineraryTimeline }
export default ItineraryTimeline
